import { collection, getDocs, getFirestore } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiBarChart2, FiClock, FiMusic, FiStar, FiTrendingUp, FiUsers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { app } from '../firebase';
import { hasPlayableAudio, normalizeTrackDocument } from '../utils/tracks';
import './Community.css';

const db = getFirestore(app);

const getTrackGenres = (track) => Array.isArray(track.genres) && track.genres.length
    ? track.genres
    : [track.genre || 'Uncategorized'];

const getViews = (track) => Number(track.viewCount || track.views || 0);
const getSales = (track) => Number(track.salesCount || 0);
const getSaves = (track) => Number(track.saveCount || track.wishlistCount || 0);

const formatPrice = (price) => {
    const numericPrice = Number(price || 0);
    return Number.isFinite(numericPrice) ? `$${numericPrice.toFixed(2)}` : '$0.00';
};

const Community = () => {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCommunity = async () => {
            setLoading(true);
            setError('');
            try {
                const snapshot = await getDocs(collection(db, 'tracks'));
                const fetchedTracks = snapshot.docs
                    .map(normalizeTrackDocument)
                    .filter((track) => track.title && track.artist && hasPlayableAudio(track));
                setTracks(fetchedTracks);
            } catch (err) {
                console.error('Error loading community:', err);
                setError('Failed to load community highlights.');
            } finally {
                setLoading(false);
            }
        };

        fetchCommunity();
    }, []);

    const producerStats = useMemo(() => {
        const artistMap = new Map();

        tracks.forEach((track) => {
            const artist = track.artist || 'Unknown Producer';
            const current = artistMap.get(artist) || {
                artist,
                userId: track.userId,
                coverImage: track.coverImage,
                tracks: 0,
                views: 0,
                sales: 0,
                saves: 0,
                genres: new Set(),
            };

            current.tracks += 1;
            current.views += getViews(track);
            current.sales += getSales(track);
            current.saves += getSaves(track);
            getTrackGenres(track).forEach((genre) => current.genres.add(genre));
            if (!current.coverImage && track.coverImage) current.coverImage = track.coverImage;
            if (!current.userId && track.userId) current.userId = track.userId;
            artistMap.set(artist, current);
        });

        return Array.from(artistMap.values())
            .map((producer) => ({
                ...producer,
                score: producer.sales * 8 + producer.views + producer.saves * 3 + producer.tracks * 2,
                genres: Array.from(producer.genres).slice(0, 3),
            }))
            .sort((a, b) => b.score - a.score);
    }, [tracks]);

    const producerOfWeek = producerStats[0] || null;
    const topProducers = producerStats.slice(0, 5);
    const trendingBeats = [...tracks]
        .sort((a, b) => (getViews(b) + getSales(b) * 10 + getSaves(b) * 4) - (getViews(a) + getSales(a) * 10 + getSaves(a) * 4))
        .slice(0, 5);
    const newestBeats = [...tracks]
        .sort((a, b) => {
            const aTime = a.uploadedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
            const bTime = b.uploadedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
        })
        .slice(0, 5);

    const communityStats = [
        { label: 'Live beats', value: tracks.length.toLocaleString() },
        { label: 'Producers', value: producerStats.length.toLocaleString() },
        { label: 'Total views', value: tracks.reduce((sum, track) => sum + getViews(track), 0).toLocaleString() },
        { label: 'Sales signals', value: tracks.reduce((sum, track) => sum + getSales(track), 0).toLocaleString() },
    ];

    const renderBeatCard = (track) => (
        <article key={track.id} className="community-beat-card">
            <div className="community-artwork">
                {track.coverImage ? <img src={track.coverImage} alt={track.title} /> : <FiMusic />}
            </div>
            <div className="community-beat-copy">
                <h3>{track.title || 'Untitled beat'}</h3>
                <p>{track.artist || 'Unknown producer'}</p>
                <div className="community-tags">
                    <span>{getTrackGenres(track).join(' / ')}</span>
                    <span>{formatPrice(track.price)}</span>
                </div>
            </div>
        </article>
    );

    return (
        <div className="community-page">
            <main className="community-shell">
                <section className="community-hero">
                    <div>
                        <span className="community-kicker"><FiUsers /> Community signal</span>
                        <h1>Find the producers artists are moving toward.</h1>
                        <p>Producer of the week, trending beats, new uploads, and early marketplace signals for live MVP testing.</p>
                        <div className="community-actions">
                            <Link to="/marketplace" className="community-primary-action">
                                Browse Beats
                                <FiArrowRight />
                            </Link>
                            <Link to="/upload" className="community-secondary-action">Upload a Beat</Link>
                        </div>
                    </div>
                    <aside className="community-week-card">
                        <span><FiStar /> Producer of the Week</span>
                        {producerOfWeek ? (
                            <>
                                <div className="producer-artwork">
                                    {producerOfWeek.coverImage ? <img src={producerOfWeek.coverImage} alt={producerOfWeek.artist} /> : <FiMusic />}
                                </div>
                                <h2>{producerOfWeek.artist}</h2>
                                <p>{producerOfWeek.tracks} beats · {producerOfWeek.views.toLocaleString()} views · {producerOfWeek.sales.toLocaleString()} sales</p>
                                <Link
                                    to={producerOfWeek.userId ? `/profile/${producerOfWeek.userId}` : `/marketplace?artist=${encodeURIComponent(producerOfWeek.artist)}`}
                                    className="community-card-link"
                                >
                                    View Producer
                                </Link>
                            </>
                        ) : (
                            <p>Upload tracks to start building the weekly producer board.</p>
                        )}
                    </aside>
                </section>

                <section className="community-stats-grid" aria-label="Community stats">
                    {communityStats.map((stat) => (
                        <div key={stat.label} className="community-stat-card">
                            <strong>{stat.value}</strong>
                            <span>{stat.label}</span>
                        </div>
                    ))}
                </section>

                {error && <div className="community-error">{error}</div>}

                {loading ? (
                    <div className="community-loading">
                        <div className="community-spinner"></div>
                        <p>Loading community...</p>
                    </div>
                ) : (
                    <>
                        <section className="community-section">
                            <div className="community-section-header">
                                <div>
                                    <span><FiTrendingUp /> Ranked by signal</span>
                                    <h2>Top Producers</h2>
                                </div>
                                <Link to="/marketplace" className="community-inline-link">View marketplace</Link>
                            </div>
                            <div className="producer-grid">
                                {topProducers.length > 0 ? topProducers.map((producer, index) => (
                                    <Link
                                        key={producer.artist}
                                        to={producer.userId ? `/profile/${producer.userId}` : `/marketplace?artist=${encodeURIComponent(producer.artist)}`}
                                        className="producer-card"
                                    >
                                        <span className="producer-rank">{index + 1}</span>
                                        <div className="producer-card-art">
                                            {producer.coverImage ? <img src={producer.coverImage} alt={producer.artist} /> : <FiMusic />}
                                        </div>
                                        <h3>{producer.artist}</h3>
                                        <p>{producer.tracks} beats · {producer.views.toLocaleString()} views</p>
                                        <div className="community-tags">
                                            {producer.genres.length ? producer.genres.map((genre) => <span key={genre}>{genre}</span>) : <span>Producer</span>}
                                        </div>
                                    </Link>
                                )) : (
                                    <div className="community-empty">No producers yet. Upload the first beat to start the board.</div>
                                )}
                            </div>
                        </section>

                        <section className="community-section">
                            <div className="community-section-header">
                                <div>
                                    <span><FiBarChart2 /> Views, saves, sales</span>
                                    <h2>Trending Beats</h2>
                                </div>
                                <Link to="/marketplace" className="community-inline-link">License beats</Link>
                            </div>
                            <div className="community-beat-grid">
                                {trendingBeats.length > 0 ? trendingBeats.map(renderBeatCard) : (
                                    <div className="community-empty">No trending beats yet.</div>
                                )}
                            </div>
                        </section>

                        <section className="community-section">
                            <div className="community-section-header">
                                <div>
                                    <span><FiClock /> Fresh catalog</span>
                                    <h2>New Uploads</h2>
                                </div>
                                <Link to="/upload" className="community-inline-link">Add yours</Link>
                            </div>
                            <div className="community-beat-grid compact">
                                {newestBeats.length > 0 ? newestBeats.map(renderBeatCard) : (
                                    <div className="community-empty">No uploads yet.</div>
                                )}
                            </div>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default Community;
